/**
 * Attendance aggregation.
 *
 * Every tally separates *recorded* marks (attending, late, excused, missing) from *unset*
 * slots. Unset slots never enter a rate's denominator, so a half-filled roll call reports
 * the rate of what was actually recorded rather than a misleadingly low number; coverage
 * reports how much of the roster was recorded.
 */

import {
  AttendanceState,
  attendanceMarkId,
  type AttendanceMark,
  type IsoDate,
  type Person,
  type PersonId,
} from './types.js';

/** Counts of each attendance state over some set of person-session slots. */
export interface StateCounts {
  attending: number;
  late: number;
  excused: number;
  missing: number;
  unset: number;
}

/** An empty tally, used as a reduction seed. */
export function emptyCounts(): StateCounts {
  return { attending: 0, late: 0, excused: 0, missing: 0, unset: 0 };
}

/** Reads the count of one state out of a tally. */
export function countOf(counts: StateCounts, state: AttendanceState | 'unset'): number {
  switch (state) {
    case AttendanceState.Attending: return counts.attending;
    case AttendanceState.Late: return counts.late;
    case AttendanceState.Excused: return counts.excused;
    case AttendanceState.Missing: return counts.missing;
    case 'unset': return counts.unset;
  }
}

/** Number of slots that carry a recorded state. */
export function recordedCount(counts: StateCounts): number {
  return counts.attending + counts.late + counts.excused + counts.missing;
}

/** Total number of slots, recorded or not. */
export function slotCount(counts: StateCounts): number {
  return recordedCount(counts) + counts.unset;
}

/**
 * Share of recorded slots that count as present, in `[0, 1]`.
 *
 * Someone who arrived late was still there, so a late mark counts as present exactly like an
 * attending one; the roll call distinguishes them for the sake of the record, not the rate.
 *
 * @param countExcusedAsPresent when true, excused absences count towards the rate.
 * @returns `null` when nothing was recorded, which callers must render as "no data" rather
 *   than as zero.
 */
export function presentRate(counts: StateCounts, countExcusedAsPresent: boolean): number | null {
  const recorded = recordedCount(counts);
  if (recorded === 0) return null;
  const present = counts.attending + counts.late + (countExcusedAsPresent ? counts.excused : 0);
  return present / recorded;
}

/**
 * Share of slots that were recorded at all, in `[0, 1]`.
 *
 * @returns `null` when there were no slots to record.
 */
export function coverageRate(counts: StateCounts): number | null {
  const total = slotCount(counts);
  return total === 0 ? null : recordedCount(counts) / total;
}

/** Adds one slot in `state` (or an unset slot) to `counts`, mutating it. */
export function addState(counts: StateCounts, state: AttendanceState | undefined): void {
  switch (state) {
    case AttendanceState.Attending: counts.attending += 1; break;
    case AttendanceState.Late: counts.late += 1; break;
    case AttendanceState.Excused: counts.excused += 1; break;
    case AttendanceState.Missing: counts.missing += 1; break;
    case undefined: counts.unset += 1; break;
  }
}

/** Adds every count of `source` into `target`, mutating it. */
export function accumulate(target: StateCounts, source: StateCounts): void {
  target.attending += source.attending;
  target.late += source.late;
  target.excused += source.excused;
  target.missing += source.missing;
  target.unset += source.unset;
}

/** Fast lookup of recorded marks by their `${personId}:${date}` key. */
export type MarkIndex = ReadonlyMap<string, AttendanceState>;

/**
 * Indexes live marks by person and date, keeping the most recently updated mark per slot.
 *
 * Soft-deleted marks are dropped, which is how a slot returns to unset.
 */
export function indexMarks(marks: readonly AttendanceMark[]): MarkIndex {
  const index = new Map<string, AttendanceState>();
  const seenAt = new Map<string, number>();
  for (const mark of marks) {
    const key = mark.id;
    const previous = seenAt.get(key);
    if (previous !== undefined && previous > mark.updatedAt) continue;
    seenAt.set(key, mark.updatedAt);
    if (mark.deletedAt !== null) index.delete(key);
    else index.set(key, mark.state);
  }
  return index;
}

/** Reads the state of one slot, or `undefined` when it is unset. */
export function stateAt(index: MarkIndex, personId: PersonId, date: IsoDate): AttendanceState | undefined {
  return index.get(attendanceMarkId(personId, date));
}

/** One session's tally across the whole roster. */
export interface SessionTally {
  date: IsoDate;
  counts: StateCounts;
  /** Present share of recorded marks, or `null` when nothing was recorded that day. */
  rate: number | null;
}

/** Tallies each session in `dates` across `people`, preserving the order of `dates`. */
export function tallySessions(
  people: readonly Person[],
  dates: readonly IsoDate[],
  marks: MarkIndex,
  countExcusedAsPresent: boolean,
): SessionTally[] {
  return dates.map((date) => {
    const counts = emptyCounts();
    for (const person of people) addState(counts, stateAt(marks, person.id, date));
    return { date, counts, rate: presentRate(counts, countExcusedAsPresent) };
  });
}

/** One person's tally across a set of sessions. */
export interface PersonTally {
  person: Person;
  counts: StateCounts;
  /** Present share of this person's recorded marks, or `null` when they have none. */
  rate: number | null;
  /**
   * Count of the most recent unbroken run of misses, ignoring unset sessions.
   *
   * Any other recorded state ends the run, including an excused absence and a late arrival.
   */
  missStreak: number;
}

/**
 * Tallies every person in `people` over `dates`.
 *
 * `dates` must be ascending; the miss streak is read from its tail.
 */
export function tallyPeople(
  people: readonly Person[],
  dates: readonly IsoDate[],
  marks: MarkIndex,
  countExcusedAsPresent: boolean,
): PersonTally[] {
  return people.map((person) => {
    const counts = emptyCounts();
    for (const date of dates) addState(counts, stateAt(marks, person.id, date));
    let missStreak = 0;
    for (let index = dates.length - 1; index >= 0; index -= 1) {
      const state = stateAt(marks, person.id, dates[index]);
      if (state === undefined) continue;
      if (state === AttendanceState.Missing) missStreak += 1;
      else break;
    }
    return { person, counts, rate: presentRate(counts, countExcusedAsPresent), missStreak };
  });
}

/** Whole-project totals over a set of sessions. */
export interface GroupSummary {
  counts: StateCounts;
  /** Present share across every recorded mark, or `null` when none exist. */
  rate: number | null;
  /** Share of person-session slots that carry a recorded mark, or `null` when there are none. */
  coverage: number | null;
  sessionCount: number;
  rosterSize: number;
}

/** Sums `tallies` into a single project-wide summary. */
export function summarize(
  tallies: readonly SessionTally[],
  rosterSize: number,
  countExcusedAsPresent: boolean,
): GroupSummary {
  const counts = emptyCounts();
  for (const tally of tallies) accumulate(counts, tally.counts);
  return {
    counts,
    rate: presentRate(counts, countExcusedAsPresent),
    coverage: coverageRate(counts),
    sessionCount: tallies.length,
    rosterSize,
  };
}

/** Formats a rate in `[0, 1]` as a whole-percent string, or an em dash when there is no data. */
export function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}
